import { useState } from "react";
import { DiseaseDto, DiseaseFormData } from "../types/disease"; // <-- Χρησιμοποιούμε το DiseaseFormData
import { diseaseService } from "../services/diseaseService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useDiseases = () => {
  // 1. GET (με useApi, cache και signal)
  const {
    data: diseases = [],
    loading: isFetching,
    error: getError,
    refetch: fetchDiseases,
    invalidateCache,
  } = useApi<DiseaseDto[]>("diseases", (signal) => diseaseService.getAll({}, signal));

  // 2. CREATE
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newDisease: DiseaseFormData) => diseaseService.create(newDisease));

  // 3. UPDATE (Το ID είναι αυστηρά string αφού είναι icd10Id)
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: string; data: DiseaseFormData }) =>
    diseaseService.update(id, data)
  );

  // 4. DELETE (με string IDs)
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: string[]) => {
    await Promise.all(ids.map((id) => diseaseService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createDisease = async (newDisease: DiseaseFormData): Promise<void> => {
    try {
      await createExecution(newDisease);
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ασθένεια αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDiseases(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateDisease = async (
    id: string,
    updatedDisease: DiseaseFormData
  ): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedDisease });
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ασθένεια ενημερώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDiseases(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteDiseases = async (ids: string[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchDiseases(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    diseases,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchDiseases: () => fetchDiseases(true),
    createDisease,
    updateDisease,
    deleteDiseases,
    modalState,
    closeModal,
  };
};